import { affiliateApi } from "../lib/axios";

/**
 * Whether this affiliate is confined to a single white-label brand.
 *
 * Served by its own endpoint rather than riding on `/affiliates/detail`, so
 * the white-label work stays off the affiliate detail payload.
 */
export interface PortalScope {
  isScoped: boolean;
  homeBrandId: string | null;
  homeBrandUsername: string | null;
  homeBrandName: string | null;
  /**
   * Branding for the scoped shell (ticket 4). The backend falls back to the
   * home brand's own identity, so a scoped affiliate always has a usable
   * name; null only when unscoped.
   */
  portalName: string | null;
  portalLogo: string | null;
  /** Home brand accent colours (FULL tier only); null keeps the default scale. */
  primaryColour: string | null;
  secondaryColour: string | null;
}

export const UNSCOPED_PORTAL: PortalScope = {
  isScoped: false,
  homeBrandId: null,
  homeBrandUsername: null,
  homeBrandName: null,
  portalName: null,
  portalLogo: null,
  primaryColour: null,
  secondaryColour: null,
};

/**
 * True when a request was refused because this affiliate's portal is scoped to
 * one white-label brand. The backend answers marketplace endpoints with a 403
 * carrying `code: "MARKETPLACE_DISABLED"`; that is a deliberate block, not a
 * failure, so callers should degrade quietly instead of showing an error.
 */
export function isMarketplaceDisabled(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: string } } })
    ?.response;
  return (
    response?.status === 403 && response?.data?.code === "MARKETPLACE_DISABLED"
  );
}

/**
 * Where a scoped affiliate stands with their home brand.
 *
 * Read separately from the scope itself, and never cached: the scope response
 * is cached server-side for a minute, and a minute of staleness here would
 * bounce an affiliate who has just joined back to the join page.
 */
export interface PortalMembership {
  isScoped: boolean;
  homeBrandUsername: string | null;
  hasEnrollment: boolean;
  hasPendingApplication: boolean;
  /** Either of the above — the affiliate has somewhere to be inside the portal. */
  hasMembership: boolean;
  /**
   * The single outstanding invitation, if any. A scoped affiliate has at most
   * one brand, so this is shown as a dashboard alert rather than its own page.
   */
  pendingInvitation: {
    id: string;
    programId: string;
    programName: string | null;
  } | null;
}

export const NO_MEMBERSHIP: PortalMembership = {
  isScoped: false,
  homeBrandUsername: null,
  hasEnrollment: false,
  hasPendingApplication: false,
  hasMembership: false,
  pendingInvitation: null,
};

export const portalScopeService = {
  async getMyScope(): Promise<PortalScope> {
    const { data } = await affiliateApi.get<PortalScope>("/portal-scope/me");
    return data;
  },

  async getMyMembership(): Promise<PortalMembership> {
    const { data } = await affiliateApi.get<PortalMembership>(
      "/portal-scope/me/membership"
    );
    return data;
  },
};
